import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { EditCampaignStyles } from "./styled";

import Input from "components/Input/index";
import Select from "components/Select/index";
import Button from "components/Buttons/index";
import Checkbox from "components/Checkbox/index";
import Textarea from "components/Input/Textarea";
import RadioLabelComponent from "components/Toggles/RadioLabelComponent";

import { useMarketplaceSlice } from "../slice/index";
import { mapApiToForm, mapFormToApi } from "utils/selectValueMapper";

import {
  approvalOptions,
  CREATOR_TYPES,
  rejectReasonOptions,
  selectOptionsMap
} from "./data";
import { HelperText } from "components/Input/styled";
import {
  validateBudgetRange,
  validateMinMaxRange,
  validateSingleBudget
} from "utils/validateMinMax";
import MultiSelectSearch from "components/MultiSelectSearch/index";

const DEFAULT_FORM = {
  approval_status: "",
  retool_comments: "",
  service_type: "",
  rejected_reason: "",
  platform: "",
  categories: [],
  languages: [],
  cities: [],
  is_pan_india: false,
  any_language: false,
  is_high_value: false,
  description: "",
  brand_info: "",
  brand: "",
  barter: false,
  creator_count_min: "",
  creator_count_max: "",
  budget_type: "",
  budget_min: "",
  budget_max: "",
  budget: "",
  is_open: false,
  creator_range: [],
  closed_via: "",
  type: "No",
  collaboration_type: []
};

const INFLUENCER_SERVICE_TYPES = [
  "influencer_marketing_agencies",
  "influencers",
  "talent_management_agencies"
];

const collaborationTypeMap = {
  barter: "Barter",
  paid: "Paid",
  others: "Others"
};

const EditCampaign = ({
  campaignState,
  handleGoBack,
  updateLoading,
  categories,
  languages,
  companies,
  cities,
  onCategorySearch,
  categoriesLoading,
  onLanguageSearch,
  languageLoading,
  onCompanySearch,
  onCitySearch,
  citiesLoading
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { actions } = useMarketplaceSlice();
  const location = useLocation();

  // Helper: map names to IDs
  const mapNamesToIds = (names = [], options = []) => {
    if (!Array.isArray(names) || !Array.isArray(options)) return [];

    const lowerNames = names
      .filter((n) => typeof n === "string")
      .map((n) => n.toLowerCase());

    return options
      .filter(
        (opt) =>
          opt?.name &&
          typeof opt.name === "string" &&
          lowerNames.includes(opt.name.toLowerCase())
      )
      .map((opt) => opt.id);
  };

  const initialState = useMemo(() => {
    if (!campaignState) return {};

    // Platform mapping
    const platform =
      campaignState.platform === "Instagram,YouTube"
        ? "Both"
        : campaignState.platform;

    return mapApiToForm({
      approval_status: campaignState.status,
      retool_comments: campaignState.retool_comments ?? "",
      service_type: campaignState.service_type,
      platform,
      categories: mapNamesToIds(campaignState.categories, categories),
      languages: mapNamesToIds(campaignState.languages, languages),
      cities: mapNamesToIds(campaignState.cities, cities),
      is_pan_india: campaignState?.is_pan_india,
      any_language: Boolean(campaignState?.any_language),
      is_high_value: campaignState?.is_high_value === "Yes" ? true : false,
      description: campaignState?.description,
      brand_info: campaignState.brand_info,
      brand: campaignState?.brand,
      barter: campaignState?.barter,
      creator_count_min: campaignState?.creator_count_min,
      creator_count_max: campaignState?.creator_count_max,
      budget_type: campaignState?.budget_type || "overall_budget",
      budget_min: campaignState?.budget_min,
      budget_max: campaignState?.budget_max,
      budget: campaignState?.budget,
      is_open:
        campaignState?.is_open === true || campaignState?.is_open === "open",
      creator_range: campaignState?.creator_range
        ? campaignState.creator_range.split(",")
        : [],
      collaboration_type:
        collaborationTypeMap[campaignState?.collaboration_type] || "",
      type:
        campaignState?.type === "Yes" || campaignState?.type === "Others"
          ? "Yes"
          : "No",
      rejected_reason: campaignState?.rejected_reason || ""
    });
  }, [campaignState, categories, languages, cities]);

  const [initialForm, setInitialForm] = useState(DEFAULT_FORM);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [isBudgetRange, setIsBudgetRange] = useState(false);
  // Track changed fields
  const [changedFields, setChangedFields] = useState({});
  const [creatorCountErrors, setCreatorCountErrors] = useState({
    min: "",
    max: ""
  });
  const [budgetErrors, setBudgetErrors] = useState({
    min: "",
    max: ""
  });
  const [budgetError, setBudgetError] = useState("");
  const [budgetTouched, setBudgetTouched] = useState(false);

  useEffect(() => {
    if (campaignState && Object.keys(initialState).length > 0) {
      setInitialForm({ ...DEFAULT_FORM, ...initialState });
      setForm({ ...DEFAULT_FORM, ...initialState });
    }
  }, []);

  const getChangedFields = (current, initial) => {
    const changed = {};

    Object.keys(current).forEach((key) => {
      const currVal =
        typeof current[key] === "string" ? current[key].trim() : current[key];

      const initVal =
        typeof initial[key] === "string" ? initial[key].trim() : initial[key];

      if (Array.isArray(currVal) && Array.isArray(initVal)) {
        if (
          currVal.length !== initVal.length ||
          currVal.some((val, idx) => val !== initVal[idx])
        ) {
          changed[key] = currVal;
        }
      } else if (currVal !== initVal) {
        changed[key] = currVal;
      }
    });

    return changed;
  };

  // Update changed fields on form change
  useEffect(() => {
    const changes = getChangedFields(form, initialForm);
    setChangedFields(changes);
  }, [form, initialForm]);

  const updateField = (key, value) => {
    setForm((prev) => {
      if (key === "creator_range") {
        const current = prev.creator_range || [];

        return {
          ...prev,
          creator_range: value.checked
            ? [...current, value.type] // add
            : current.filter((t) => t !== value.type) // remove
        };
      }

      return { ...prev, [key]: value };
    });
  };

  const isEmpty = (value) => {
    if (Array.isArray(value)) return value.length === 0;
    return value === undefined || value === null || value === "";
  };

  const serviceType = form?.service_type?.toLowerCase();

  const isInfluencer = INFLUENCER_SERVICE_TYPES.includes(serviceType);

  const hasServiceType = !!serviceType;

  const hasRequiredErrors = useMemo(() => {
    const requiredFields = ["approval_status", "service_type", "description"];

    if (form?.approval_status === "rejected" && serviceType === "influencers") {
      requiredFields.push("rejected_reason");
    }

    if (serviceType === "influencers") {
      requiredFields.push("collaboration_type");
    }

    return requiredFields.some((field) => isEmpty(form[field]));
  }, [form, serviceType]);

  const handleSaveChanges = () => {
    if (hasRequiredErrors) return;

    if (Object.keys(changedFields).length === 0) {
      return;
    }

    const mappedFields = mapFormToApi(changedFields);

    const payload = {
      ...mappedFields,
      ...(changedFields.creator_range && {
        creator_range: changedFields.creator_range.join(",")
      }),
      ...(changedFields.languages && {
        languages: changedFields.languages
      }),
      ...(changedFields.collaboration_type !== undefined && {
        collaboration_type: changedFields.collaboration_type
      }),
      ...(changedFields.rejected_reason !== undefined && {
        rejected_reason: changedFields.rejected_reason
      })
    };

    dispatch(
      actions.updateMarketplace({
        navigate,
        id: campaignState.id,
        payload,
        location
      })
    );
  };

  useEffect(() => {
    if (form.budget_type) {
      setBudgetTouched(false);
    }
  }, [form.budget_type]);

  useEffect(() => {
    if (form.budget_min || form.budget_max) {
      setIsBudgetRange(true);
    } else {
      setIsBudgetRange(false);
    }
  }, [form.budget_min, form.budget_max]);

  // Dropdown options
  const categoryOptions = categories?.map((cat) => ({
    value: cat.id,
    label: cat.name
  }));

  const languageOptions = languages?.map((cat) => ({
    value: cat.id,
    label: cat.name
  }));

  const cityOptions = cities?.map((cat) => ({
    value: cat.id,
    label: cat.name
  }));

  const handleBudgetChange = (field, val) => {
    const nextValues = {
      budget_min: field === "budget_min" ? val : form.budget_min,
      budget_max: field === "budget_max" ? val : form.budget_max
    };

    updateField(field, val);

    const errors = validateBudgetRange(
      nextValues.budget_min,
      nextValues.budget_max
    );

    setBudgetErrors(errors);
  };

  const handleSingleBudgetChange = (val) => {
    updateField("budget", val);

    updateField("budget_min", null);
    updateField("budget_max", null);

    setBudgetError(validateSingleBudget(val));
  };

  const handleCreatorCountChange = (field, val) => {
    const nextValues = {
      creator_count_min:
        field === "creator_count_min" ? val : form.creator_count_min,
      creator_count_max:
        field === "creator_count_max" ? val : form.creator_count_max
    };

    updateField(field, val);

    const errors = validateMinMaxRange(
      nextValues.creator_count_min,
      nextValues.creator_count_max
    );

    setCreatorCountErrors(errors);
  };

  const creatorCountCommonError =
    creatorCountErrors.max || creatorCountErrors.min;

  const budgetCommonErrors = budgetErrors.max || budgetErrors.min;

  const hasValidationErrors = useMemo(() => {
    return (
      !!creatorCountErrors.min ||
      !!creatorCountErrors.max ||
      !!budgetErrors.min ||
      !!budgetErrors.max ||
      !!budgetError
    );
  }, [creatorCountErrors, budgetErrors, budgetError]);

  const rejectReasonOptionsForDisplay = rejectReasonOptions.map((item) => ({
    ...item,
    value: item.apiValue // use apiValue for matching with API response
  }));

  return (
    <>
      <EditCampaignStyles>
        {/* user-details */}
        <div className="details-info-wrapper">
          <h2>User Details</h2>

          <div className="user-details-content-wrapper">
            {/* first-batch */}
            <div className="user-details-content">
              <div className="user-details-single-content">
                <span className="small-header">ID</span>
                <p className="user-id">{campaignState?.user_id}</p>
              </div>

              <div className="user-details-single-content">
                <span className="small-header">Phone Number</span>
                <p>{campaignState?.user_phone_number || "phone number"}</p>
              </div>
            </div>
            {/* second-batch */}
            <div className="user-details-content">
              <div className="user-details-single-content">
                <span className="small-header">User Name</span>
                <p>{campaignState?.user_name || "Sample UserName"}</p>
              </div>

              <div className="user-details-single-content">
                <span className="small-header">Company</span>
                <p>{campaignState?.user_company || "Sample Company"}</p>
              </div>
            </div>
            {/* third-batch */}
            <div className="user-details-content">
              <div className="user-details-single-content">
                <span className="small-header">Designation</span>
                <p>{campaignState?.user_designation || "Designation"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Approval Information */}
        <div className="details-info-wrapper">
          <h2>Approval Information</h2>

          <div className="user-details-content-wrapper approval request-details">
            <Select
              id="approval_status"
              label="Approval Status"
              required
              options={approvalOptions}
              value={form?.approval_status || ""}
              onChange={(val) => updateField("approval_status", val)}
              disabled
            />

            {form?.approval_status === "rejected" &&
              serviceType === "influencers" && (
                <Select
                  id="rejected_reason"
                  label="Reject Reason"
                  required
                  options={rejectReasonOptionsForDisplay}
                  value={form?.rejected_reason || ""}
                  onChange={(val) => updateField("rejected_reason", val)}
                  disabled
                />
              )}

            {serviceType !== "influencers" &&
              form?.approval_status === "approved" && (
                <div className="user-details-content disabled">
                  <div className="user-details-single-content high-deal">
                    <h4>Move to Others</h4>
                    <div className="radio-wrapper">
                      <RadioLabelComponent
                        labelHeader="Yes"
                        checked={form.type === "Yes"}
                        onChange={() => updateField("type", "Yes")}
                        disabled
                      />
                      <RadioLabelComponent
                        labelHeader="No"
                        checked={form.type === "No"}
                        onChange={() => updateField("type", "No")}
                        description
                      />
                    </div>
                  </div>
                </div>
              )}

            {serviceType === "influencers" && (
              <Select
                id="collaboration_type"
                label="Collaboration Type"
                options={selectOptionsMap?.collaboration_type}
                required={serviceType === "influencers"}
                value={form.collaboration_type || ""}
                onChange={(val) => updateField("collaboration_type", val)}
                disabled
              />
            )}
          </div>
        </div>

        {/* Request Details */}
        <div className="details-info-wrapper">
          <h2>Request Details</h2>

          {!hasServiceType && (
            <div className="user-details-content-wrapper request-details">
              <div className="user-details-content">
                <div className="user-details-single-content">
                  <Select
                    id="service_type"
                    label="Service Type"
                    required
                    options={selectOptionsMap?.service_type}
                    value={form?.service_type || ""}
                    onChange={(val) => updateField("service_type", val)}
                    disabled
                  />
                </div>

                <div className="user-details-single-content">
                  <Textarea
                    id="description"
                    label="Description"
                    placeholder="Add description"
                    rows={6}
                    value={form?.description || ""}
                    onChange={(e) => updateField("description", e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {hasServiceType && isInfluencer && (
            <div className="user-details-content-wrapper request-details">
              <div className="user-details-content">
                <div className="user-details-single-content">
                  <Select
                    id="service_type"
                    label="Service Type"
                    required
                    options={selectOptionsMap?.service_type}
                    value={form?.service_type || ""}
                    onChange={(val) => updateField("service_type", val)}
                    disabled
                  />
                </div>

                <div className="user-details-single-content">
                  <Input
                    id="target_audience"
                    label="Target Audience"
                    value="Agencies"
                    disabled
                  />
                </div>

                <div className="user-details-single-content platform">
                  <h4>Platform</h4>
                  <RadioLabelComponent
                    labelHeader="Instagram"
                    checked={form.platform === "Instagram"}
                    onChange={() => updateField("platform", "Instagram")}
                  />

                  <RadioLabelComponent
                    labelHeader="YouTube"
                    checked={form.platform === "YouTube"}
                    onChange={() => updateField("platform", "YouTube")}
                  />

                  <RadioLabelComponent
                    labelHeader="Both"
                    checked={form.platform === "Both"}
                    onChange={() => updateField("platform", "Both")}
                  />
                </div>

                <div className="user-details-single-content">
                  {/* <Select
                    id="categories"
                    label="Categories"
                    options={categoryOptions}
                    value={form?.categories}
                    onChange={(val) => updateField("categories", val)}
                    isMulti={true}
                    maxSelect={5}
                    onSearch={onCategorySearch}
                  /> */}

                  <MultiSelectSearch
                    id="categories"
                    label="Categories (Upto 5)"
                    options={categoryOptions}
                    value={form?.categories}
                    onChange={(val) => updateField("categories", val)}
                    helperText="Helper Text"
                    placeholder="Select Value"
                    maxSelect={5}
                    onSearch={onCategorySearch}
                    loading={categoriesLoading}
                  />
                </div>

                <div className="user-details-single-content toggle-wrapper">
                  {/* <Select
                    id="city"
                    label="City"
                    options={cityOptions}
                    value={form?.cities || ""}
                    onChange={(val) => updateField("cities", val)}
                    isMulti={true}
                    maxSelect={5}
                    disabled={form?.is_pan_india}
                  /> */}

                  <MultiSelectSearch
                    id="city"
                    label="City (Upto 5)"
                    options={cityOptions}
                    value={form?.cities || ""}
                    onChange={(val) => updateField("cities", val)}
                    isMulti={true}
                    maxSelect={5}
                    disabled={form?.is_pan_india}
                    onSearch={onCitySearch}
                    loading={citiesLoading}
                  />

                  <Checkbox
                    checked={!!form.is_pan_india}
                    // disabled={form.cities}
                    size="M"
                    label="Pan India"
                    color="neutral"
                    onChange={(val) => {
                      updateField("is_pan_india", val);

                      if (val) {
                        updateField("cities", []);
                      }
                    }}
                  />
                </div>

                <div className="user-details-single-content toggle-wrapper">
                  {/* <Select
                    id="languages"
                    label="Languages"
                    options={languageOptions}
                    value={form?.languages || []}
                    onChange={(val) => updateField("languages", val)}
                    isMulti={true}
                    maxSelect={5}
                    disabled={form.any_language}
                  /> */}
                  <MultiSelectSearch
                    id="languages"
                    label="Languages (Upto 5)"
                    options={languageOptions}
                    value={form?.languages || []}
                    onChange={(val) => updateField("languages", val)}
                    isMulti={true}
                    maxSelect={5}
                    disabled={form.any_language}
                    onSearch={onLanguageSearch}
                    loading={languageLoading}
                  />

                  <Checkbox
                    checked={!!form.any_language}
                    size="M"
                    label="Any Language"
                    color="neutral"
                    onChange={(val) => {
                      updateField("any_language", val);

                      if (val) {
                        updateField("languages", []);
                      }
                    }}
                  />
                </div>

                <div className="user-details-single-content high-deal">
                  <h4>High Deal</h4>
                  <div className="radio-wrapper">
                    <RadioLabelComponent
                      labelHeader="Yes"
                      checked={form.is_high_value === true}
                      onChange={() => updateField("is_high_value", true)}
                    />
                    <RadioLabelComponent
                      labelHeader="No"
                      checked={form.is_high_value === false}
                      onChange={() => updateField("is_high_value", false)}
                    />
                  </div>
                </div>

                <div className="user-details-single-content">
                  <Textarea
                    id="description"
                    label="Description"
                    placeholder="Add description"
                    rows={6}
                    value={form?.description || ""}
                    onChange={(e) => updateField("description", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="user-details-content">
                <div className="user-details-single-content">
                  <div className="multiple-fields">
                    <Select
                      id="brand type"
                      label="Brand Type"
                      options={selectOptionsMap?.brand_info}
                      value={form?.brand_info || ""}
                      onChange={(val) => updateField("brand_info", val)}
                    />

                    <Input
                      id="brand_name"
                      placeholder={
                        form?.brand_info === "brand_type"
                          ? "Brand Type"
                          : "Brand Name"
                      }
                      value={form?.brand || ""}
                      onChange={(e) => updateField("brand", e.target.value)}
                    />
                  </div>
                </div>

                <div className="user-details-single-content">
                  <div className="multiple-fields">
                    <Input
                      id="creator_count_min"
                      placeholder="Min value"
                      type="number"
                      label="Required Creators"
                      value={form.creator_count_min || ""}
                      hasError={!!creatorCountErrors.min}
                      onChange={(e) =>
                        handleCreatorCountChange(
                          "creator_count_min",
                          e.target.value
                        )
                      }
                    />

                    <Input
                      id="creator_count_max"
                      placeholder="Max value"
                      type="number"
                      value={form.creator_count_max || ""}
                      hasError={!!creatorCountErrors.max}
                      onChange={(e) =>
                        handleCreatorCountChange(
                          "creator_count_max",
                          e.target.value
                        )
                      }
                    />
                  </div>

                  {creatorCountCommonError && (
                    <HelperText hasError={!!creatorCountErrors.max} size="M">
                      {creatorCountCommonError}
                    </HelperText>
                  )}

                  <Checkbox
                    checked={form.barter}
                    size="M"
                    label="Barter"
                    color="neutral"
                    onChange={(val) => {
                      updateField("barter", val);

                      if (val) {
                        updateField("budget", null);
                        updateField("budget_min", null);
                        updateField("budget_max", null);
                        updateField("budget_type", null);
                        setBudgetErrors({ min: "", max: "" });
                      }
                    }}
                  />
                </div>

                {!form?.barter ? (
                  <>
                    <div className="user-details-single-content">
                      <div className="multiple-fields">
                        <Select
                          id="budget_type"
                          label="Budget Type"
                          options={selectOptionsMap?.budget_type}
                          value={form.budget_type || ""}
                          onChange={(val) => updateField("budget_type", val)}
                          disabled={form.barter}
                        />
                      </div>
                    </div>

                    <div className="user-details-single-content">
                      {!isBudgetRange && (
                        <div className="multiple-fields">
                          <Input
                            id="budget"
                            label="Budget"
                            placeholder="Enter a budget"
                            leftIcon="₹"
                            type="number"
                            value={form.budget || ""}
                            hasError={
                              (budgetTouched && !form.budget_type) ||
                              !!budgetError
                            }
                            onFocus={() => setBudgetTouched(true)}
                            helperText={budgetError}
                            onChange={(e) =>
                              handleSingleBudgetChange(e.target.value)
                            }
                            disabled={
                              (budgetTouched && !form.budget_type) ||
                              form.barter
                            }
                          />
                        </div>
                      )}

                      {isBudgetRange && (
                        <div className="multiple-fields">
                          <Input
                            id="budget_min"
                            label="Budget"
                            leftIcon="₹"
                            type="number"
                            placeholder="Enter min Budget"
                            value={form.budget_min || ""}
                            hasError={
                              (budgetTouched && !form.budget_type) ||
                              !!budgetErrors.min
                            }
                            onFocus={() => setBudgetTouched(true)}
                            disabled={
                              (budgetTouched && !form.budget_type) ||
                              form.barter
                            }
                            onChange={(e) =>
                              handleBudgetChange("budget_min", e.target.value)
                            }
                          />

                          <Input
                            id="budget_max"
                            leftIcon="₹"
                            type="number"
                            placeholder="Enter max Budget"
                            value={form.budget_max || ""}
                            hasError={
                              (budgetTouched && !form.budget_type) ||
                              !!budgetErrors.max
                            }
                            onFocus={() => setBudgetTouched(true)}
                            disabled={
                              (budgetTouched && !form.budget_type) ||
                              form.barter
                            }
                            onChange={(e) =>
                              handleBudgetChange("budget_max", e.target.value)
                            }
                          />
                        </div>
                      )}

                      {budgetTouched && !form.budget_type && (
                        <HelperText hasError size="M">
                          Choose the budget type
                        </HelperText>
                      )}

                      {budgetCommonErrors && (
                        <HelperText hasError={budgetCommonErrors} size="M">
                          {budgetCommonErrors}
                        </HelperText>
                      )}

                      <Checkbox
                        label="Add as Range"
                        checked={isBudgetRange}
                        onChange={(val) => {
                          setIsBudgetRange(val);

                          if (val) {
                            // switched to RANGE
                            updateField("budget", null);
                          } else {
                            // switched to SINGLE
                            updateField("budget_min", null);
                            updateField("budget_max", null);
                          }

                          // clear errors
                          setBudgetError("");
                          setBudgetErrors({ min: "", max: "" });
                        }}
                        disabled={form.barter}
                      />
                    </div>
                  </>
                ) : null}

                <div className="user-details-single-content">
                  <h4>
                    Creator Type{" "}
                    {!form.platform ? "(Choose any one platform)" : ""}
                  </h4>

                  {CREATOR_TYPES.map((type) => (
                    <Checkbox
                      key={type}
                      label={type}
                      checked={form.creator_range?.includes(type)}
                      disabled={!form.platform}
                      onChange={(checked) =>
                        updateField("creator_range", { type, checked })
                      }
                    />
                  ))}
                </div>

                <div className="user-details-single-content">
                  <Select
                    id="closed_via"
                    label="Closed Via"
                    options={selectOptionsMap?.closed_via}
                    value={form.closed_via || ""}
                    onChange={(val) => updateField("closed_via", val)}
                    disabled={form.is_open}
                  />
                  <Checkbox
                    checked={!!form.is_open}
                    size="M"
                    label="Still Open"
                    color="neutral"
                    onChange={(val) => updateField("is_open", val)}
                    disabled={form.closed_via}
                  />
                </div>

                <Textarea
                  id="retool_comments"
                  label="Comment"
                  placeholder="Add comments here"
                  rows={6}
                  value={form?.retool_comments || ""}
                  onChange={(e) =>
                    updateField("retool_comments", e.target.value)
                  }
                />
              </div>
            </div>
          )}

          {hasServiceType && !isInfluencer && (
            <>
              <div className="user-details-content-wrapper request-details">
                <div className="user-details-content isInfluencer">
                  <div className="user-details-single-content">
                    <Select
                      id="service_type"
                      label="Service Type"
                      required
                      options={selectOptionsMap?.service_type}
                      value={form?.service_type || ""}
                      onChange={(val) => updateField("service_type", val)}
                      disabled
                    />
                  </div>

                  <div className="user-details-single-content">
                    <Textarea
                      id="description"
                      label="Description"
                      placeholder="Add description"
                      rows={6}
                      value={form?.description || ""}
                      onChange={(e) =>
                        updateField("description", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </EditCampaignStyles>

      <div className="campaign-footer-wrapper">
        <Button variant="secondary" onClick={handleGoBack}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveChanges}
          loading={updateLoading}
          disabled={
            updateLoading ||
            hasRequiredErrors ||
            hasValidationErrors ||
            Object.keys(changedFields).length === 0
          }
        >
          Save Changes
        </Button>
      </div>
    </>
  );
};

export default EditCampaign;
